class Wishlist extends HTMLElement {
  constructor() {
    super();
    this.storageKey = 'shopify_wishlist';
    this.customerId = this.dataset.customerId || null;
    this.init();
  }

  init() {
    this.wishlist = this.loadWishlist();
    this.updateUI();
    this.setupEventListeners();
  }

  loadWishlist() {
    if (this.customerId) {
      // For logged-in customers, we'll use localStorage for now
      // In production, you'd fetch from customer metafields via API
      const stored = localStorage.getItem(`${this.storageKey}_${this.customerId}`);
      return stored ? JSON.parse(stored) : [];
    } else {
      // For guests, use localStorage
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    }
  }

  saveWishlist() {
    if (this.customerId) {
      localStorage.setItem(`${this.storageKey}_${this.customerId}`, JSON.stringify(this.wishlist));
      // In production, also save to customer metafields via API
    } else {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlist));
    }
  }

  isInWishlist(productId) {
    return this.wishlist.some(item => item.id === productId);
  }

  addToWishlist(product) {
    if (!this.isInWishlist(product.id)) {
      this.wishlist.push({
        id: product.id,
        title: product.title,
        handle: product.handle,
        price: product.price,
        comparePrice: product.comparePrice || null,
        image: product.image,
        url: product.url,
        variantId: product.variantId || null
      });
      this.saveWishlist();
      this.updateUI();
      this.dispatchEvent(new CustomEvent('wishlist:added', { detail: product }));
    }
  }

  removeFromWishlist(productId) {
    this.wishlist = this.wishlist.filter(item => item.id !== productId);
    this.saveWishlist();
    this.updateUI();
    this.dispatchEvent(new CustomEvent('wishlist:removed', { detail: { id: productId } }));
  }

  toggleWishlist(product) {
    if (this.isInWishlist(product.id)) {
      this.removeFromWishlist(product.id);
      this.showNotification('Removed from wishlist');
    } else {
      this.addToWishlist(product);
      this.showNotification('Added to wishlist');
    }
  }

  showNotification(message) {
    const existing = document.getElementById('wishlist-notification');
    if (existing) existing.remove();
    const el = document.createElement('div');
    el.id = 'wishlist-notification';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.textContent = message;
    el.style.cssText = 'position:fixed;bottom:20px;left:50%;transform:translateX(-50%);background:#333;color:#fff;padding:12px 20px;border-radius:6px;font-size:14px;font-weight:500;z-index:10000;box-shadow:0 4px 12px rgba(0,0,0,0.2);transition:opacity 0.2s ease;';
    document.body.appendChild(el);
    setTimeout(() => {
      el.style.opacity = '0';
      setTimeout(() => el.remove(), 200);
    }, 2500);
  }

  getWishlist() {
    return this.wishlist;
  }

  getWishlistCount() {
    return this.wishlist.length;
  }

  updateUI() {
    // Update all wishlist buttons
    document.querySelectorAll('wishlist-button').forEach(button => {
      const productId = button.dataset.productId;
      if (productId) {
        button.classList.toggle('is-active', this.isInWishlist(productId));
      }
    });

    // Update wishlist count if element exists
    const countElement = document.querySelector('[data-wishlist-count]');
    if (countElement) {
      countElement.textContent = this.getWishlistCount();
    }

    // Update header badge count
    const badgeElement = document.querySelector('[data-wishlist-count-badge]');
    if (badgeElement) {
      const count = this.getWishlistCount();
      if (count > 0) {
        badgeElement.textContent = count;
        badgeElement.style.display = 'flex';
      } else {
        badgeElement.style.display = 'none';
      }
    }
  }

  setupEventListeners() {
    // Listen for wishlist button clicks
    document.addEventListener('click', (e) => {
      const button = e.target.closest('wishlist-button');
      if (button) {
        e.preventDefault();
        e.stopPropagation();
        const product = {
          id: button.dataset.productId,
          title: button.dataset.productTitle,
          handle: button.dataset.productHandle,
          price: button.dataset.productPrice,
          comparePrice: button.dataset.productComparePrice || null,
          image: button.dataset.productImage,
          url: button.dataset.productUrl,
          variantId: button.dataset.productVariantId || null
        };
        this.toggleWishlist(product);
      }
    });

    // Also handle keyboard events for accessibility
    document.addEventListener('keydown', (e) => {
      const button = e.target.closest('wishlist-button');
      if (button && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        e.stopPropagation();
        button.click();
      }
    });
  }
}

class WishlistButton extends HTMLElement {
  connectedCallback() {
    const wishlist = document.querySelector('wishlist-manager');
    if (wishlist) {
      wishlist.updateUI();
    }
  }
}

if (!customElements.get('wishlist-manager')) {
  customElements.define('wishlist-manager', Wishlist);
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

